import { SITE_COOKIE_MAX_AGE } from '~/constants/cookies';

export type CookieOptions = {
  maxAge?: number;
  expires?: Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
};

const SAME_SITE: Record<NonNullable<CookieOptions['sameSite']>, string> = {
  strict: 'Strict',
  lax: 'Lax',
  none: 'None'
};

export const getCookie = (name: string): string | undefined => {
  if (globalThis.document === undefined) return undefined;
  const pattern = new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)}=([^;]*)`);
  const match = pattern.exec(globalThis.document.cookie);
  if (!match) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

export const setCookie = (name: string, value: string | boolean | number, options?: CookieOptions): void => {
  if (globalThis.document === undefined) return;
  let cookie = `${name}=${encodeURIComponent(String(value))}`;
  if (options?.maxAge !== undefined) cookie += `; Max-Age=${options.maxAge}`;
  if (options?.expires) cookie += `; Expires=${options.expires.toUTCString()}`;
  cookie += `; Path=${options?.path ?? '/'}`;
  if (options?.domain) cookie += `; Domain=${options.domain}`;
  if (options?.secure) cookie += `; Secure`;
  if (options?.sameSite) cookie += `; SameSite=${SAME_SITE[options.sameSite]}`;
  // biome-ignore lint/suspicious/noDocumentCookie: centralised write point
  globalThis.document.cookie = cookie;
};

export const deleteCookie = (name: string, options?: Pick<CookieOptions, 'path' | 'domain'>): void => {
  setCookie(name, '', { ...options, maxAge: -1 });
};

export const hasCookie = (name: string): boolean => {
  return getCookie(name) !== undefined;
};

export const resolveCookieDomain = (host: string | null | undefined): string | undefined =>
  host?.includes('byway.travel') ? '.byway.travel' : undefined;

export const setSiteCookie = (name: string, value: string | boolean | number): void => {
  const domain = resolveCookieDomain(globalThis.location?.hostname);
  if (domain) deleteCookie(name);
  setCookie(name, value, { maxAge: SITE_COOKIE_MAX_AGE, path: '/', sameSite: 'lax', domain });
};

export const deleteSiteCookie = (name: string): void => {
  const domain = resolveCookieDomain(globalThis.location?.hostname);
  deleteCookie(name);
  if (domain) deleteCookie(name, { domain });
};
